import { useState, useEffect, useMemo } from "react";
import { useAdminLanguage } from "../../layouts/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Check, X, Trash2, Star, ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const { t } = useAdminLanguage();

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await fetch(`${API}/api/admin/testimonials`, { credentials: 'include' });
      if (res.ok) setTestimonials(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />;
  };

  const sorted = useMemo(() => {
    return [...testimonials].sort((a, b) => {
      let va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [testimonials, sortKey, sortDir]);

  const handleModerate = async (id, action) => {
    try {
      const res = await fetch(`${API}/api/admin/testimonials/${id}/moderate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }), credentials: "include"
      });
      if (res.ok) { toast.success(`Temoignage ${action === "approve" ? "approuve" : "rejete"}`); fetchTestimonials(); }
    } catch (e) { toast.error(t("connection_error")); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      const res = await fetch(`${API}/api/admin/testimonials/${id}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success(t("testimonial_deleted")); fetchTestimonials(); }
    } catch (e) { toast.error(t("connection_error")); }
  };

  const statusColor = (status) => {
    switch (status) {
      case "approved": return "bg-emerald-500/10 text-emerald-400";
      case "rejected": return "bg-red-500/10 text-red-400";
      default: return "bg-yellow-500/10 text-yellow-400";
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">{t("testimonials")}</h1>
          <p className="text-zinc-500 text-sm">Moderez les temoignages des utilisateurs.</p>
        </div>
        <Badge className="bg-zinc-800 text-zinc-400">{testimonials.filter(t => t.status === "pending").length} en attente</Badge>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("author_name")}>
                  <span className="flex items-center">{t("author")}<SortIcon col="author_name" /></span>
                </TableHead>
                <TableHead className="text-zinc-400">{t("content")}</TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("rating")}>
                  <span className="flex items-center">{t("rating")}<SortIcon col="rating" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("status")}>
                  <span className="flex items-center">{t("status")}<SortIcon col="status" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("created_at")}>
                  <span className="flex items-center">{t("date")}<SortIcon col="created_at" /></span>
                </TableHead>
                <TableHead className="text-right text-zinc-400">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(item => (
                <TableRow key={item.testimonial_id} className="border-zinc-800 hover:bg-zinc-800/50">
                  <TableCell>
                    <div>
                      <span className="text-white text-sm font-medium">{item.author_name}</span>
                      <p className="text-zinc-500 text-xs">{item.author_role}{item.author_company ? ` - ${item.author_company}` : ""}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-zinc-300 text-sm max-w-xs truncate">{item.content}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < (item.rating || 0) ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`} />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColor(item.status)}>{t(item.status) || item.status}</Badge>
                  </TableCell>
                  <TableCell className="text-zinc-500 text-xs">{item.created_at ? new Date(item.created_at).toLocaleDateString() : "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {item.status === "pending" && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-400 hover:text-emerald-300" onClick={() => handleModerate(item.testimonial_id, "approve")} title={t("approve")}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => handleModerate(item.testimonial_id, "reject")} title={t("reject")}>
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-red-400" onClick={() => handleDelete(item.testimonial_id)} title={t("delete")}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">{t("no_data")}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
